import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keepPreviousData } from '@tanstack/react-query';
import { useDebounceValue, useLocalStorage } from 'usehooks-ts';

import {
  cn,
  DataTable,
  DateRangePicker,
  Flex,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  MultiCombobox,
  Spinner,
  Text,
  EmptyState,
  type IDataTableRowClickedEvent,
} from '@kognitos/lattice';
import { AccessDenied } from '@/shared/components';
import { useListRunsInfinite } from '@/generated/hooks/jeeves/v1/api';
import { useArchiveRun } from '../use-archive-run';
import {
  type V1Run,
  V1InvocationSource,
} from '@/generated/hooks/jeeves/models';
import { useGetAutomation } from '@/generated/hooks/grimoire/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { CursorPagination } from '@/shared/components/CursorPagination';
import { RUN_STAGE } from '@/shared/constants';
import { getRoutePath } from '@/routes/util';
import {
  parseResourceName,
  FilterBuilder,
  serializeParams,
} from '@/shared/utils';
import {
  parseDateString,
  dateToISOString,
  normalizeToStartOfDay,
  normalizeToEndOfDay,
} from '@/shared/utils/datetime-utils';
import { getRunsListColumns, runStateKeys } from '../consts';
import {
  getContentSearchError,
  hasRunContentSearchDetails,
  mergeContentMatches,
} from '../content-search';
import { ContentSearchErrorAlert } from './ContentSearchErrorAlert';
import {
  FIXED_COLUMN_MAX_WIDTH,
  FIXED_COLUMN_MIN_WIDTH,
  OUTPUT_COLUMN_MAX_WIDTH,
  OUTPUT_COLUMN_MIN_WIDTH,
  RUNS_TABLE_FIXED_COLUMN_IDS,
} from '../runs-table-widths';
import { useBatchGetRunExecutionTimes } from '@/generated/hooks/bishop/events/v1/api';
import type { V1BatchGetRunExecutionTimesResponseRunExecutionTimes } from '@/generated/hooks/bishop/models';
import { checkIsAccessDeniedError } from '@/shared/utils/error-handler';
import type { ColumnResizedEvent } from 'ag-grid-community';
import type { TResolvedValue } from '@/modules/outputs/types';
import { downloadOutput } from '@/modules/outputs/utils/download';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import {} from '@/modules/outputs/components/output-presentation';
import './automation-runs-table.css';

const DEFAULT_RUNS_PAGE_SIZE = 15;
// Each search is a server-side scan, so don't send one per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

const clampOutputWidth = (width: number) =>
  Math.min(OUTPUT_COLUMN_MAX_WIDTH, Math.max(OUTPUT_COLUMN_MIN_WIDTH, width));
const clampFixedColumnWidth = (width: number) =>
  Math.min(FIXED_COLUMN_MAX_WIDTH, Math.max(FIXED_COLUMN_MIN_WIDTH, width));
const FIXED_COLUMN_IDS: string[] = Object.values(RUNS_TABLE_FIXED_COLUMN_IDS);

/**
 * Builds a reader for persisted column widths that drops anything that is not
 * a number and clamps the rest. Stored values come from a previous session, so
 * they can predate the current clamp range or have been hand-edited.
 */
const makeWidthsDeserializer =
  (clamp: (width: number) => number) =>
  (raw: string): Record<string, number> => {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      return Object.fromEntries(
        Object.entries(parsed)
          .filter(([, width]) => typeof width === 'number')
          .map(([name, width]) => [name, clamp(width as number)]),
      );
    } catch {
      return {};
    }
  };
const deserializeOutputWidths = makeWidthsDeserializer(clampOutputWidth);
const deserializeFixedColumnWidths = makeWidthsDeserializer(
  clampFixedColumnWidth,
);

/** Output spec with at least a name for column header */
type OutputSpec = { name: string };

interface AutomationRunsListProps {
  automationId: string;
  /** Called from the empty-state CTA to start a run. */
  onRun?: () => void;
  /**
   * Called when a run's "View run" action is triggered. The run-history dialog
   * uses this to close itself — important when viewing the run you're already
   * on, where navigation is a no-op and nothing else would happen.
   */
  onViewRun?: () => void;
  isRunLoading?: boolean;
  /** Output specs for dynamic output columns; when absent, derived from runs */
  outputSpecs?: OutputSpec[];
  /** When true, shows draft-stage runs and links to the draft run route. */
  isDraft?: boolean;
  /** Whether to render the internal "Runs" title (hidden when embedded in a dialog). */
  showTitle?: boolean;
  /** Replaces the default "Runs" title in the header row, which the filters share. */
  title?: ReactNode;
  /** Replaces the unfiltered empty state's title, e.g. to name the stage being listed. */
  emptyTitle?: string;
  /**
   * The asset workspace's actions, e.g. Triggers and Run. With these the
   * filters move to their own row under the title, and the actions end it.
   */
  headerActions?: ReactNode;
  /**
   * Dialog-layout flag. When true, uses the run-history dialog presentation
   * (popover surface, pinned pagination, content-hugging height). The Status +
   * Date range filter row is identical in both layouts. When false (default),
   * uses the dashboard page layout.
   */
  showSearch?: boolean;
  /** Number of runs fetched per page. Defaults to 15. */
  pageSize?: number;
  /**
   * Whether filter state is persisted to URL search params. Defaults to true
   * for the published-automation page (deep-linkable filters). The dialog
   * passes false so its filters stay local and don't pollute the page URL.
   */
  persistFiltersInUrl?: boolean;
  /** Between the filters and the table, e.g. the asset workspace's attention banner. */
  beforeTable?: ReactNode;
  /** After the search box, before the status filter, e.g. the asset workspace's Live / Draft filter. */
  extraFilters?: ReactNode;
}

/**
 * Every output key seen on the loaded runs. Catches outputs the manifest can't
 * list (a dynamically built `set_output` name) and automations without one.
 */
function getOutputNamesFromRuns(runs: (V1Run | undefined)[]): string[] {
  const keys = new Set<string>();
  for (const run of runs) {
    if (!run) continue;
    const outputs = run.state?.completed?.outputs;
    if (outputs && typeof outputs === 'object') {
      for (const key of Object.keys(outputs)) {
        keys.add(key);
      }
    }
  }
  return [...keys].sort();
}

export function AutomationRunsList({
  automationId,
  onRun,
  onViewRun,
  isRunLoading = false,
  outputSpecs,
  isDraft = false,
  showTitle = true,
  showSearch = false,
  pageSize = DEFAULT_RUNS_PAGE_SIZE,
  persistFiltersInUrl = true,
  beforeTable,
  extraFilters,
  title,
  emptyTitle,
  headerActions,
}: AutomationRunsListProps) {
  const { t: tCommon } = useTranslation('common');
  const { t: tAutomation } = useTranslation('automation');

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Local filter state used when filters should NOT persist to the URL (e.g.
  // when embedded in the run-history dialog). When persistFiltersInUrl is true
  // these are ignored in favor of the URL search params.
  const [localRunStates, setLocalRunStates] = useState<string[]>([]);
  const [localSearch, setLocalSearch] = useState('');
  const [localFromDate, setLocalFromDate] = useState<Date | undefined>(
    undefined,
  );
  const [localToDate, setLocalToDate] = useState<Date | undefined>(undefined);

  const { data: automationData } = useGetAutomation(
    currentOrganizationId || '',
    currentWorkspaceId || '',
    automationId,
    undefined,
    {
      query: {
        enabled: Boolean(
          automationId && currentOrganizationId && currentWorkspaceId,
        ),
      },
    },
  );

  const [previewData, setPreviewData] = useState<{
    name: string;
    value: TResolvedValue;
    navigationItems: Array<{ name: string; value: TResolvedValue }>;
  } | null>(null);
  // Widths are per automation, so the key changes as the user moves between
  // automations; useLocalStorage re-reads on key change and persists writes.
  const [outputWidths, setOutputWidths] = useLocalStorage<
    Record<string, number>
  >(
    `bumblebee:automation-runs:output-widths:v1:${currentOrganizationId}:${currentWorkspaceId}:${automationId}`,
    {},
    { deserializer: deserializeOutputWidths },
  );
  // Fixed columns (Start time through Actions) are per automation too.
  const [columnWidths, setColumnWidths] = useLocalStorage<
    Record<string, number>
  >(
    `bumblebee:automation-runs:column-widths:v1:${currentOrganizationId}:${currentWorkspaceId}:${automationId}`,
    {},
    { deserializer: deserializeFixedColumnWidths },
  );

  // Get browser's timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Read filter values from the URL params or local state depending on mode.
  const fromDateParam = searchParams.get('fromDate');
  const toDateParam = searchParams.get('toDate');
  // Status filter is multiselect: a list of run-state keys. In URL mode it's a
  // comma-separated `runState` param; an empty list means "no status filter"
  // (show all).
  const selectedRunStates = persistFiltersInUrl
    ? (searchParams.get('runState')?.split(',').filter(Boolean) ?? [])
    : localRunStates;
  const fromDate = persistFiltersInUrl
    ? fromDateParam
      ? parseDateString(fromDateParam)
      : undefined
    : localFromDate;
  const toDate = persistFiltersInUrl
    ? toDateParam
      ? parseDateString(toDateParam)
      : undefined
    : localToDate;
  const searchTerm = persistFiltersInUrl
    ? (searchParams.get('q') ?? '')
    : localSearch;
  const [debouncedSearchTerm] = useDebounceValue(
    searchTerm,
    SEARCH_DEBOUNCE_MS,
  );

  // Updates a single URL search param (or removes it when value is empty).
  const updateUrlParam = (key: string, value: string | undefined) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  // Runs filter: exclude archived, stage, non-test invocation, date range, and
  // any selected statuses (matched as an OR of runState clauses).
  const buildRunsFilter = () => {
    const filter = FilterBuilder.create();
    filter.equals('archived', 'false');
    filter.equals('stage', isDraft ? RUN_STAGE.DRAFT : RUN_STAGE.PUBLISHED);
    filter.notEquals(
      'invocation_source',
      V1InvocationSource.INVOCATION_SOURCE_TEST,
    );
    if (fromDate) {
      const startOfDay = normalizeToStartOfDay(fromDate, timezone);
      filter.greaterThanOrEqual('create_time', startOfDay.toISOString());
    }
    if (toDate) {
      const endOfDay = normalizeToEndOfDay(toDate, timezone);
      filter.lessThanOrEqual('create_time', endOfDay.toISOString());
    }
    if (selectedRunStates.length > 0) {
      filter.or(
        ...selectedRunStates.map(state =>
          FilterBuilder.create().equals('runState', state),
        ),
      );
    }
    if (debouncedSearchTerm.trim()) {
      // `has` already escapes quotes; escapeFilterValue too would double it.
      filter.has('search', debouncedSearchTerm.trim());
    }
    return filter.build();
  };

  const baseEnabled = Boolean(
    automationId && currentOrganizationId && currentWorkspaceId,
  );

  const runsQuery = useListRunsInfinite(
    currentOrganizationId,
    currentWorkspaceId,
    automationId!,
    {
      page_size: pageSize,
      filter: buildRunsFilter(),
      order_by: 'create_time desc',
    },
    {
      query: {
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: true,
        staleTime: 0,
        retry: false,
        enabled: baseEnabled,
        // A search keeps the current rows on screen while it runs.
        placeholderData: debouncedSearchTerm.trim()
          ? keepPreviousData
          : undefined,
        getNextPageParam: (lastPage: { data: { next_page_token?: string } }) =>
          lastPage.data.next_page_token,
      },
    },
  );

  // The last response that loaded, so a search the server refuses for reading
  // file contents (429/504) can leave the previous results in place.
  const [lastLoadedRuns, setLastLoadedRuns] = useState({
    automationId,
    data: runsQuery.data,
  });
  if (
    runsQuery.isSuccess &&
    !runsQuery.isPlaceholderData &&
    runsQuery.data !== lastLoadedRuns.data
  ) {
    setLastLoadedRuns({ automationId, data: runsQuery.data });
  }
  const contentSearchError =
    runsQuery.isError && debouncedSearchTerm.trim()
      ? getContentSearchError(runsQuery.error)
      : undefined;
  const loadedRuns = contentSearchError
    ? (runsQuery.data ??
      (lastLoadedRuns.automationId === automationId
        ? lastLoadedRuns.data
        : undefined))
    : runsQuery.data;

  // Runs archived this session, hidden from the table immediately (optimistic)
  // until the refetch drops them from the server response.
  const [archivedRunNames, setArchivedRunNames] = useState<Set<string>>(
    new Set(),
  );

  // Reset here, not via a remount, so focus stays on the stage filter.
  const [prevIsDraft, setPrevIsDraft] = useState(isDraft);
  if (prevIsDraft !== isDraft) {
    setPrevIsDraft(isDraft);
    setCurrentPage(0);
    setPreviewData(null);
    setArchivedRunNames(new Set());
  }

  const runs = (
    loadedRuns?.pages.flatMap(page => page.data.runs ?? []) ?? []
  ).filter(run => !run?.name || !archivedRunNames.has(run.name));
  const contentMatches = mergeContentMatches(loadedRuns?.pages);
  // Judged over every loaded run, not the page, so paging doesn't resize columns.
  const showsContentSearchDetails = runs.some(run =>
    hasRunContentSearchDetails(
      run,
      run?.name ? contentMatches[run.name] : undefined,
    ),
  );
  const lastPage = loadedRuns?.pages[loadedRuns.pages.length - 1];
  const hasMore = Boolean(lastPage?.data.next_page_token);

  const isRunsLoading = runsQuery.isLoading && runsQuery.isFetching;
  const isRunsError = runsQuery.isError && !contentSearchError;
  const runsError = runsQuery.error;
  const isRunsFetchingNextPage = runsQuery.isFetchingNextPage;
  const isRunsRefetching = runsQuery.isRefetching;
  // Any in-flight runs fetch. Drives the table overlay and blocks page nav so
  // a page change can't be issued while the previous refetch is still running.
  const isRunsBusy =
    isRunsLoading || isRunsFetchingNextPage || isRunsRefetching;
  const refetchRuns = () => {
    runsQuery.refetch();
  };

  // Paging a refused search would only repeat it; Retry or a new term does that.
  const canGoNext =
    !contentSearchError &&
    (runs.length > (currentPage + 1) * pageSize || hasMore);
  const canGoPrev = !contentSearchError && currentPage > 0;

  // Client-side pagination over the fetched runs. The best knowable total is
  // the filled pages, plus one more while the stream has more to fetch.
  const knownPages = Math.max(1, Math.ceil(runs.length / pageSize));
  const totalPages = Math.max(1, knownPages + (hasMore ? 1 : 0));

  // Every page change refetches so the user always sees current server data,
  // never a stale cached slice. Note refetch() on an infinite query refetches
  // all accumulated pages sequentially, so this gets progressively slower the
  // deeper the user has paged.
  const handleNextPage = () => {
    if (isRunsBusy) return;
    if (runs.length > (currentPage + 1) * pageSize) {
      setCurrentPage(prev => prev + 1);
      runsQuery.refetch();
    } else if (hasMore) {
      runsQuery.fetchNextPage().then(() => {
        setCurrentPage(prev => prev + 1);
      });
    }
  };

  const handlePrevPage = () => {
    if (isRunsBusy) return;
    if (canGoPrev) {
      setCurrentPage(prev => prev - 1);
      runsQuery.refetch();
    }
  };

  const currentPageData = runs.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );
  const manifestOutputNames = (
    automationData?.data?.code_manifest?.outputs ?? []
  )
    .map(output => output.name)
    .filter((name): name is string => Boolean(name));
  const outputNames =
    outputSpecs?.map(spec => spec.name) ??
    Array.from(
      new Set([...manifestOutputNames, ...getOutputNamesFromRuns(runs)]),
    );

  // AG Grid lays columns out from `colDef.width` in pixels, so a drag has to be
  // recorded or the column returns to its default width the next time the
  // table mounts. Fixed columns and output columns are stored separately.
  const handleColumnResized = useCallback(
    (event: ColumnResizedEvent<V1Run>) => {
      if (!event.finished || event.source !== 'uiColumnResized') return;
      // One drag can resize several columns, e.g. a column and its neighbour.
      const columns = event.columns ?? (event.column ? [event.column] : []);
      const resizedFixed: Record<string, number> = {};
      const resizedOutputs: Record<string, number> = {};
      for (const column of columns) {
        const columnId = column.getColId();
        const width = column.getActualWidth();
        if (FIXED_COLUMN_IDS.includes(columnId)) {
          resizedFixed[columnId] = clampFixedColumnWidth(width);
        } else if (columnId.startsWith('output:')) {
          resizedOutputs[columnId.slice('output:'.length)] =
            clampOutputWidth(width);
        }
      }
      if (Object.keys(resizedFixed).length > 0) {
        setColumnWidths(currentWidths => ({
          ...currentWidths,
          ...resizedFixed,
        }));
      }
      if (Object.keys(resizedOutputs).length === 0) return;
      setOutputWidths(currentWidths => {
        const nextWidths: Record<string, number> = {
          ...currentWidths,
          ...resizedOutputs,
        };
        // Keep only the outputs this automation still has so the stored entry
        // cannot grow unbounded as outputs are added or renamed.
        return Object.fromEntries(
          outputNames
            .filter(name => nextWidths[name] !== undefined)
            .map(name => [name, nextWidths[name]]),
        );
      });
    },
    [outputNames, setOutputWidths, setColumnWidths],
  );

  const currentPageRunNames = currentPageData
    .map(run => run?.name)
    .filter((name): name is string => Boolean(name));

  const { data: executionTimesData } = useBatchGetRunExecutionTimes(
    currentOrganizationId || '',
    currentWorkspaceId || '',
    { names: currentPageRunNames },
    {
      query: {
        enabled: currentPageRunNames.length > 0,
      },
      axios: { paramsSerializer: serializeParams },
    },
  );

  const executionTimesMap: V1BatchGetRunExecutionTimesResponseRunExecutionTimes =
    executionTimesData?.data?.run_execution_times ?? {};

  const { archiveRun, isArchiving } = useArchiveRun(automationId);

  const handleArchiveRun = async (run: V1Run) => {
    if (!(await archiveRun(run)) || !run.name) return;
    const runName = run.name;
    setArchivedRunNames(prev => new Set(prev).add(runName));
  };

  const handleViewRun = (run: IDataTableRowClickedEvent<V1Run>) => {
    const { run: runId } = parseResourceName(run.data?.name);
    if (automationId && runId) {
      navigate(
        isDraft
          ? getRoutePath('DRAFT_RUN', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              draftId: automationId,
              runId,
            })
          : getRoutePath('AUTOMATION_RUN', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              automationId,
              runId,
            }),
      );
      // Close the run-history dialog. Navigating to the run you're already on
      // is a no-op, so without this nothing would happen for the current run.
      onViewRun?.();
    }
  };

  const handleRunStateChange = (values: string[]) => {
    if (persistFiltersInUrl) {
      updateUrlParam(
        'runState',
        values.length > 0 ? values.join(',') : undefined,
      );
    } else {
      setLocalRunStates(values);
    }
    // Reset to first page when filter changes
    setCurrentPage(0);
  };

  // Single Date range control updating both endpoints together. When persisting
  // to the URL this MUST be one setSearchParams call: react-router's functional
  // updater closes over the current render's params, so two back-to-back
  // updateUrlParam calls each navigate from the same base and the second drops
  // the first's change (fromDate would be lost, leaving only toDate).
  const handleDateRangeChange = (
    range: { from: Date | undefined; to?: Date | undefined } | undefined,
  ) => {
    const from = range?.from;
    const to = range?.to;
    if (persistFiltersInUrl) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (from) {
          newParams.set('fromDate', dateToISOString(from));
        } else {
          newParams.delete('fromDate');
        }
        if (to) {
          newParams.set('toDate', dateToISOString(to));
        } else {
          newParams.delete('toDate');
        }
        return newParams;
      });
    } else {
      setLocalFromDate(from);
      setLocalToDate(to);
    }
    // Reset to first page when filter changes.
    setCurrentPage(0);
  };

  const handleSearchChange = (value: string) => {
    if (persistFiltersInUrl) {
      updateUrlParam('q', value || undefined);
    } else {
      setLocalSearch(value);
    }
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    if (persistFiltersInUrl) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('runState');
        newParams.delete('fromDate');
        newParams.delete('toDate');
        newParams.delete('q');
        return newParams;
      });
    } else {
      setLocalRunStates([]);
      setLocalFromDate(undefined);
      setLocalToDate(undefined);
      setLocalSearch('');
    }
    setCurrentPage(0);
  };

  const hasFilters =
    selectedRunStates.length > 0 || fromDate || toDate || Boolean(searchTerm);

  // In the asset workspace the list fills the view, so its states centre in it.
  const stateClassName = title ? 'flex-1' : undefined;

  const renderContent = () => {
    if (isRunsLoading) {
      return (
        <Flex
          justify="center"
          align="center"
          width="full"
          height="full"
          className={stateClassName}
        >
          <Spinner size="xl" />
        </Flex>
      );
    }

    if (isRunsError) {
      const isAccessDeniedError = checkIsAccessDeniedError(runsError);

      return (
        <Flex
          justify="center"
          align="center"
          width="full"
          height="full"
          className={stateClassName}
        >
          {isAccessDeniedError ? (
            <AccessDenied className="max-w-md" />
          ) : (
            <EmptyState
              icon="AlertCircle"
              title={tAutomation('automation.runs.error.title')}
              description={tAutomation('automation.runs.error.description')}
              actionLabel={tCommon('common.action.reload')}
              actionIcon="RefreshCw"
              onAction={refetchRuns}
              actionLoading={isRunsRefetching}
              className="max-w-md"
            />
          )}
        </Flex>
      );
    }

    const contentSearchAlert = contentSearchError && (
      <ContentSearchErrorAlert
        error={contentSearchError}
        showsPreviousResults={runs.length > 0}
        onRetry={refetchRuns}
        isRetrying={runsQuery.isFetching}
      />
    );

    // The refused search never ran, so "no runs found" would be wrong.
    if (contentSearchAlert && runs.length === 0) {
      return contentSearchAlert;
    }

    // Runs used to derive output columns and detect the empty state.
    const allRuns = runs;
    if (allRuns.length === 0) {
      return (
        <Flex
          justify="center"
          align="center"
          width="full"
          height="full"
          className={stateClassName}
        >
          <EmptyState
            icon={hasFilters ? 'Filter' : 'Play'}
            title={
              hasFilters
                ? tAutomation('automation.runs.empty.filtered.title')
                : (emptyTitle ?? tAutomation('automation.runs.empty.title'))
            }
            description={
              hasFilters
                ? undefined
                : tAutomation('automation.runs.empty.description')
            }
            actionLabel={
              hasFilters
                ? tCommon('common.action.clearFilters')
                : tCommon('common.action.run')
            }
            onAction={hasFilters ? handleClearFilters : onRun}
            actionLoading={hasFilters ? false : isRunLoading}
            actionIcon={hasFilters ? 'X' : 'Play'}
            className="max-w-md"
          />
        </Flex>
      );
    }

    return (
      <Flex
        direction="column"
        gap={4}
        width="full"
        // In the dialog layout, fill the available height and push the
        // pagination to the bottom of the modal (per design); the dashboard
        // keeps its natural top-aligned flow.
        justify={showSearch ? 'between' : undefined}
        className={showSearch ? 'min-w-0 flex-1 min-h-0' : 'min-w-0'}
      >
        {contentSearchAlert}
        <div
          className={cn(
            'automation-runs-table w-full overflow-x-auto overscroll-x-none overflow-y-hidden rounded-md border border-border [--ag-cell-horizontal-padding:12px]',
            showSearch
              ? '[--runs-table-surface:var(--popover)]'
              : '[--runs-table-surface:var(--layout)]',
          )}
          data-testid="automation-runs-table"
        >
          <DataTable<V1Run>
            data={currentPageData as V1Run[]}
            columns={getRunsListColumns(
              handleViewRun,
              handleArchiveRun,
              isArchiving,
              outputNames,
              executionTimesMap,
              {
                contentMatches,
                showsContentSearchDetails,
                widths: outputWidths,
                columnWidths,
                onOpenOutput: (name, value, resolved) =>
                  setPreviewData({
                    name,
                    value,
                    navigationItems: outputNames
                      .filter(outputName => resolved[outputName] !== undefined)
                      .map(outputName => ({
                        name: outputName,
                        value: resolved[outputName],
                      })),
                  }),
              },
            )}
            background="transparent"
            loadingOverlayComponent={() => <Spinner size="xl" />}
            loading={isRunsBusy}
            gridOptions={{
              domLayout: 'autoHeight',
              rowHeight: 112,
              // Stable row identity by name so paging updates rows in place
              // instead of rebuilding/re-measuring the whole autoHeight body.
              getRowId: ({ data }) => data.name ?? '',
              // Don't animate row add/remove on page change; the slide/fade of
              // autoHeight rows reads as jitter when switching pages.
              animateRows: false,
              suppressCellFocus: false,
              // Render all columns and sync header/body scroll so the header
              // doesn't lag the body during horizontal scrolling.
              suppressColumnVirtualisation: true,
              suppressAnimationFrame: true,
              onColumnResized: handleColumnResized,
            }}
          />
        </div>
        <CursorPagination
          currentPage={currentPage}
          canGoNext={!!canGoNext}
          canGoPrev={canGoPrev}
          totalPages={totalPages}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          isLoading={isRunsRefetching}
          isFetchingNextPage={isRunsFetchingNextPage}
        />
      </Flex>
    );
  };

  // Compact trigger label: "All" when nothing is selected, the single status
  const statusSelect = (
    <MultiCombobox
      options={Object.entries(runStateKeys).map(([value, label]) => ({
        value,
        label,
      }))}
      value={selectedRunStates}
      onValueChange={handleRunStateChange}
      placeholder={tAutomation('automation.runs.status')}
      searchPlaceholder={tAutomation('automation.runs.status')}
      // Fixed trigger width keeps the compact filter row and a stable popover
      // anchor; popoverClassName sizes the dropdown.
      buttonClassName="w-44"
      popoverClassName="w-56"
      showClear
    />
  );

  const filters = (
    <Flex
      gap={2}
      align="center"
      justify="start"
      // Wraps onto further rows rather than overflowing a narrow page.
      className={cn('flex-wrap', showTitle && !headerActions && 'ml-auto')}
      data-testid="runs-list-filters"
    >
      <InputGroup className="w-[240px]">
        <InputGroupAddon>
          <Icon type="Search" />
        </InputGroupAddon>
        <InputGroupInput
          value={searchTerm}
          onChange={event => handleSearchChange(event.target.value)}
          placeholder={tAutomation('automation.runs.searchPlaceholder')}
          // Keeps the typed term out of Statsig's input auto-capture.
          className="statsig-no-capture"
          data-testid="runs-list-search"
        />
      </InputGroup>
      {extraFilters}
      {statusSelect}
      <DateRangePicker
        dateRange={{ from: fromDate, to: toDate }}
        onDateRangeChange={handleDateRangeChange}
        placeholder={tAutomation('automation.runs.dateRange')}
        format="MMM D, YYYY"
        // Content-hugging compact filter (the DS default is a fixed w-[280px]).
        buttonClassName=" max-w-60 w-auto"
        showClear
      />
    </Flex>
  );

  return (
    <Flex
      direction="column"
      // px-1.5 pt-1.5 in the dialog layout keeps the search/date-range focus
      // rings from being clipped by the dialog's overflow boundary.
      className={
        showSearch
          ? 'w-full px-1.5 pt-1.5 flex-1 min-h-0'
          : // In the asset workspace the body already pads the top, and a
            // full-height cap would push the pagination past its bottom padding.
            cn('w-full min-h-[400px]', title ? 'flex-1' : 'h-full mt-5')
      }
      // A section title from the asset workspace sets a roomier rhythm between
      // the header row, the attention banner and the table.
      gap={title ? 4 : 2}
    >
      {/* Status + Date range filters, shared by the dialog and the dashboard.
          Each pill reveals its own centered clear (X) overlay on hover. */}
      {showTitle ? (
        <Flex
          align="center"
          justify="between"
          width="full"
          data-testid="runs-list-header"
        >
          {title ?? (
            <Text level="small" weight="medium" className="text-base">
              {tAutomation('automation.runs.title')}
            </Text>
          )}
          {!headerActions && filters}
        </Flex>
      ) : (
        filters
      )}
      {showTitle && headerActions && (
        // The filters' row, with the actions at its end.
        <Flex
          align="center"
          justify="between"
          gap={2}
          width="full"
          className="flex-wrap"
        >
          {filters}
          <Flex
            align="center"
            gap={2}
            className="shrink-0"
            data-testid="runs-list-header-actions"
          >
            {headerActions}
          </Flex>
        </Flex>
      )}
      {beforeTable}
      {renderContent()}
      {previewData && (
        <PreviewLayover
          isOpen
          outputName={previewData.name}
          outputValue={previewData.value}
          onClose={() => setPreviewData(null)}
          onDownload={downloadOutput}
          navigationItems={previewData.navigationItems}
        />
      )}
    </Flex>
  );
}
